import { modelData } from 'app/lib/modelData'
import type { UpdateTaskInput } from 'app/generated/gql/graphql'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UPDATE_TASK_MUTATION } from 'app/components/tasks/graphql'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { ContactJson } from 'app/types/entities'
import type { Mutation } from 'app/generated/graphql'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

export type CardTask = {
  id: string
  title?: string
  description?: string
  status?: string
  dueDate?: string
  ownerId?: string
}

const TASK_FIELDS = `id title description status dueDate ownerId contactId locationId createdById`

export type UseContactTasksReturn = {
  /** Open tasks, most overdue first. */
  open: CardTask[]
  /** Closed tasks, most recently due first. */
  done: CardTask[]
  loading: boolean
  /** Task ids with a write in flight. */
  saving: string[]
  openCount: number
  overdueCount: number
  /** The one to lead with on the card: most overdue, else soonest due. */
  next: CardTask | undefined
  setStatus: (id: string, status: `open` | `closed`) => void
  reschedule: (id: string, dueDate: string) => void
  refresh: () => void
}

export function isOverdue(task: CardTask): boolean {
  return !!task.dueDate && task.status === `open` && moment(task.dueDate).isBefore(moment(), `day`)
}

/**
 * A contact's tasks for the focus card: the Tasks tile reads the summary, the
 * Tasks panel works the list. One fetch serves both so the tile can never
 * disagree with the panel behind it.
 *
 * One-shot per contact, not a urql subscription — see `useContactMessages` for
 * why a live query in this component tree is off limits.
 */
export function useContactTasks(contact?: ContactJson): UseContactTasksReturn {
  const [tasks, setTasks] = useState<CardTask[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState<string[]>([])
  const generation = useRef(0)
  const contactId = contact?.id

  const load = useCallback(
    ({ quiet }: { quiet?: boolean } = {}) => {
      generation.current += 1
      const token = generation.current
      if (!contactId) {
        setTasks([])
        setLoading(false)
        return
      }
      if (!quiet) setLoading(true)
      bg(
        gqlFetchList<CardTask>(`tasks`, { search: `contactId:${contactId}`, pagination: { limit: 100 } }, TASK_FIELDS)
          .then(({ hits }) => {
            if (token !== generation.current) return
            setTasks(hits ?? [])
            setLoading(false)
          })
          .catch((err) => {
            if (token !== generation.current) return
            logError(`Action Center failed to load contact tasks`, err)
            setLoading(false)
          }),
      )
    },
    [contactId],
  )

  useEffect(() => {
    setTasks([])
    load()
  }, [contactId, load])

  /** Optimistic write: the card's other mutations run ~2.5s on this API. */
  const patch = useCallback((id: string, changes: UpdateTaskInput) => {
    setSaving((current) => [...current, id])
    setTasks((current) => current.map((task) => (task.id === id ? { ...task, ...modelData(changes) } : task)))
    bg(
      gqlMutation(UPDATE_TASK_MUTATION, { id, input: changes })
        .then((result) => {
          if (result.error) logError(`Action Center failed to update task`, result.error)
        })
        .catch((err) => logError(`Action Center failed to update task`, err))
        .finally(() => setSaving((current) => current.filter((value) => value !== id))),
    )
  }, [])

  const setStatus = useCallback((id: string, status: `open` | `closed`) => patch(id, { status }), [patch])
  const reschedule = useCallback((id: string, dueDate: string) => patch(id, { dueDate }), [patch])

  const { open, done, next, overdueCount } = useMemo(() => {
    const byDueAsc = (a: CardTask, b: CardTask) => moment(a.dueDate ?? 0).valueOf() - moment(b.dueDate ?? 0).valueOf()
    const openTasks = tasks.filter((task) => task.status === `open`).sort(byDueAsc)
    const doneTasks = tasks.filter((task) => task.status !== `open`).sort((a, b) => byDueAsc(b, a))
    return {
      open: openTasks,
      done: doneTasks,
      // Sorted by due date ascending, so the first open task is the most
      // overdue when anything is late and the soonest due otherwise.
      next: openTasks[0],
      overdueCount: openTasks.filter(isOverdue).length,
    }
  }, [tasks])

  const refresh = useCallback(() => load({ quiet: true }), [load])

  return {
    open,
    done,
    loading,
    saving,
    openCount: open.length,
    overdueCount,
    next,
    setStatus,
    reschedule,
    refresh,
  }
}
